
import React, { useMemo, useState } from "react";
import PreviewDrawer from "./PreviewDrawer";

const TYPES = ["issue","position","for","against","risk","evidence"];

export default function MoveComposer({ mapId, moves, onPost }) {
  const [type, setType] = useState("position");
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [confidence, setConfidence] = useState(50);
  const [parentId, setParentId] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const positions = useMemo(
    () => moves.filter(m => m.type === "position"),
    [moves]
  );
  const issue = useMemo(
    () => moves.find(m => m.type === "issue"),
    [moves]
  );

  const validAttachTargets = useMemo(() => {
    if (type === "issue") return [];
    if (type === "position") return [issue, ...positions].filter(Boolean);
    return positions;
  }, [type, issue, positions]);

  function reset() {
    setText(""); setLink(""); setConfidence(50); setParentId(null); setPreviewOpen(false);
  }

  async function handlePost() {
    const payload = {
      type: type === "for" ? "for" : type, // already plain
      text, link: link || null,
      confidence: Number(confidence),
      parent_id: type === "issue" ? null : parentId
    };
    await onPost(payload);
    reset();
  }

  const parentLabel = useMemo(() => {
    const target = [...(issue ? [issue] : []), ...positions].find(x => x.id === parentId);
    return target ? `${String(target.type).toUpperCase()}: ${target.text.slice(0, 64)}` : "";
  }, [parentId, issue, positions]);

  return (
    <div className="hm-composer">
      <div className="hm-type-row">
        {TYPES.map(t => (
          <button
            key={t}
            className={`chip ${type===t ? `active t-${t}` : ""}`}
            onClick={() => setType(t)}
            aria-pressed={type===t}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="hm-fields">
        <textarea
          value={text}
          onChange={(e)=>setText(e.target.value.slice(0,240))}
          placeholder="Write the move (≤ 240 chars)…"
          rows={3}
        />
        <input
          type="url"
          value={link}
          onChange={(e)=>setLink(e.target.value)}
          placeholder="Optional: Add a link (https://…)"
        />
        <label className="hm-conf">
          <span>Confidence: {Math.round(confidence)}%</span>
          <input type="range" min="0" max="100" value={confidence} onChange={(e)=>setConfidence(e.target.value)} />
        </label>
      </div>

      <div className="hm-attach">
        {type !== "issue" && (
          <>
            <button className="btn subtle" onClick={()=>setAttachOpen(v=>!v)}>
              {parentId ? "Change attachment" : "Attach to…"}
            </button>
            {attachOpen && (
              <div className="attach-sheet" onClick={()=>setAttachOpen(false)}>
                <div className="attach-panel" onClick={(e)=>e.stopPropagation()}>
                  <h4>Attach to…</h4>
                  <ul>
                    {validAttachTargets.map(t => (
                      <li key={t.id}>
                        <button
                          className={`attach-item ${parentId === t.id ? "selected" : ""}`}
                          onClick={()=>{ setParentId(t.id); setAttachOpen(false); }}
                        >
                          <span className={`chip t-${t.type}`}>{String(t.type).toUpperCase()}</span>
                          <span className="attach-text">{t.text}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}

        <div className="hm-actions">
          <button
            className="btn outline"
            disabled={!text.trim() || (type!=="issue" && !parentId)}
            onClick={()=>setPreviewOpen(true)}
          >Preview</button>
        </div>
      </div>

      <PreviewDrawer
        open={previewOpen}
        onClose={()=>setPreviewOpen(false)}
        onPost={handlePost}
        preview={{ type, text, link, confidence, parentLabel }}
      />
    </div>
  );
}
